import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Send, MessageSquare } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface Update {
  id: string;
  content: string;
  created_at: string;
  user_id: string;
  email?: string;
}

interface ChallengeUpdatesTimelineProps {
  challengeId: string;
  canPost: boolean;
}

const ChallengeUpdatesTimeline: React.FC<ChallengeUpdatesTimelineProps> = ({
  challengeId,
  canPost,
}) => {
  const [updates, setUpdates] = useState<Update[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUpdate, setNewUpdate] = useState("");
  const [posting, setPosting] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    fetchUpdates();
    getCurrentUser();
    subscribeToUpdates();
  }, [challengeId]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUserId(user?.id || null);
  };

  const fetchUpdates = async () => {
    try {
      const { data: updatesData, error } = await supabase
        .from("challenge_updates")
        .select("id, content, created_at, user_id")
        .eq("challenge_id", challengeId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // Fetch emails
      const userIds = updatesData?.map((u) => u.user_id) || [];
      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, email")
        .in("id", userIds);

      const profileMap = new Map(profiles?.map((p) => [p.id, p.email]));

      const enrichedUpdates = (updatesData || []).map((u) => ({
        ...u,
        email: profileMap.get(u.user_id) || "Unknown",
      }));

      setUpdates(enrichedUpdates);
    } catch (error) {
      console.error("Error fetching updates:", error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToUpdates = () => {
    const channel = supabase
      .channel(`challenge-updates-${challengeId}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "challenge_updates",
          filter: `challenge_id=eq.${challengeId}`,
        },
        () => fetchUpdates()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const postUpdate = async () => {
    if (!newUpdate.trim() || !currentUserId) return;

    setPosting(true);
    try {
      const { error } = await supabase.from("challenge_updates").insert({
        challenge_id: challengeId,
        user_id: currentUserId,
        content: newUpdate.trim(),
      });

      if (error) throw error;

      setNewUpdate("");
      toast.success("Update posted");
      fetchUpdates();
    } catch (error) {
      console.error("Error posting update:", error);
      toast.error("Failed to post update");
    } finally {
      setPosting(false);
    }
  };

  const getInitials = (email: string) => {
    return email.slice(0, 2).toUpperCase();
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Post new update */}
      {canPost && currentUserId && (
        <div className="space-y-2 p-4 bg-muted/50 rounded-lg">
          <Textarea
            placeholder="Share a progress update with the community..."
            value={newUpdate}
            onChange={(e) => setNewUpdate(e.target.value)}
            rows={3}
          />
          <div className="flex justify-end">
            <Button
              onClick={postUpdate}
              disabled={!newUpdate.trim() || posting}
              size="sm"
            >
              <Send className="h-4 w-4 mr-2" />
              {posting ? "Posting..." : "Post Update"}
            </Button>
          </div>
        </div>
      )}

      {/* Updates list */}
      {updates.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No updates yet</p>
          {canPost && (
            <p className="text-sm">Be the first to share progress!</p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {updates.map((update) => (
            <div
              key={update.id}
              className="flex gap-3 p-4 bg-muted/30 rounded-lg"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="text-xs bg-primary/20 text-primary">
                  {getInitials(update.email || "")}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-medium text-sm text-foreground">
                    {update.email}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(update.created_at), {
                      addSuffix: true,
                    })}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                  {update.content}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChallengeUpdatesTimeline;
